/**
 * Quick Log — log any communication in under 20 seconds from anywhere in the app.
 *
 * Trigger: Cmd/Ctrl + L, or the "+" button in the sidebar.
 * Flow: search for who → pick type → one line of notes → save.
 *
 * The fastest data entry the platform has.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { pb, escapeFilterValue } from '../lib/pocketbase'
import { logCall, logEmail, logMeeting, logNote, logText, createActivity, TouchNotStampedError } from '../api/activities'
import { showToast } from './Toast'
import Drawer from './elite/Drawer'
import { Group, Field, Impact } from './elite/fields'
import styles from './QuickLog.module.css'

type EntityResult = {
  id: string
  type: 'client' | 'prospect' | 'contact'
  name: string
  subtitle: string
}

type CommType = 'call' | 'email' | 'meeting' | 'note' | 'text'

const COMM_TYPES: { value: CommType; label: string; key: string }[] = [
  { value: 'call',    label: 'Call',    key: 'C' },
  { value: 'email',   label: 'Email',   key: 'E' },
  { value: 'meeting', label: 'Meeting', key: 'M' },
  { value: 'note',    label: 'Note',    key: 'N' },
  { value: 'text',    label: 'Text',    key: 'T' },
]

interface QuickLogProps {
  isOpen: boolean
  onClose: () => void
}

export default function QuickLog({ isOpen, onClose }: QuickLogProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<EntityResult[]>([])
  const [selected, setSelected] = useState<EntityResult | null>(null)
  const [commType, setCommType] = useState<CommType>('call')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [searching, setSearching] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setResults([])
      setSelected(null)
      setCommType('call')
      setNotes('')
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [isOpen, onClose])

  // Debounced search
  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.length < 2) { setResults([]); return }

    const agencyId = (pb.authStore.model as any)?.agency_id
    if (!agencyId) return

    setSearching(true)
    try {
      const esc = escapeFilterValue(q)
      const agencyFilter = `agency_id = "${agencyId}"`

      const [clients, prospects, contacts] = await Promise.allSettled([
        pb.collection('clients').getList(1, 5, {
          filter: `${agencyFilter} && full_name ~ "${esc}"`,
          fields: 'id,full_name,team_name,sport',
          sort: 'full_name',
        }),
        pb.collection('prospects').getList(1, 5, {
          filter: `${agencyFilter} && full_name ~ "${esc}"`,
          fields: 'id,full_name,school,sport',
          sort: 'full_name',
        }),
        pb.collection('contacts').getList(1, 5, {
          filter: `${agencyFilter} && (first_name ~ "${esc}" || last_name ~ "${esc}")`,
          fields: 'id,first_name,last_name,title,organization',
          sort: 'first_name',
        }),
      ])

      const merged: EntityResult[] = []

      if (clients.status === 'fulfilled') {
        clients.value.items.forEach(c => merged.push({
          id: c.id, type: 'client',
          name: c.full_name,
          subtitle: [c.team_name, c.sport].filter(Boolean).join(' · '),
        }))
      }
      if (prospects.status === 'fulfilled') {
        prospects.value.items.forEach(p => merged.push({
          id: p.id, type: 'prospect',
          name: p.full_name,
          subtitle: [p.school, p.sport].filter(Boolean).join(' · ') || 'Prospect',
        }))
      }
      if (contacts.status === 'fulfilled') {
        contacts.value.items.forEach(c => merged.push({
          id: c.id, type: 'contact',
          name: `${c.first_name} ${c.last_name}`.trim(),
          subtitle: [c.title, c.organization].filter(Boolean).join(' · ') || 'Contact',
        }))
      }

      setResults(merged)
    } catch {
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  const handleQueryChange = (val: string) => {
    setQuery(val)
    setSelected(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(val), 250)
  }

  const handleSelect = (entity: EntityResult) => {
    setSelected(entity)
    setResults([])
    setQuery(entity.name)
    setTimeout(() => notesRef.current?.focus(), 50)
  }

  const handleSave = async () => {
    if (!selected || saving) return
    setSaving(true)

    try {
      // Who it was with. The last-contact stamp is no longer done here: the
      // loggers do it, in one format, and only for a conversation — a note
      // used to warm the person too, as if writing something down were
      // speaking to them.
      const entityIdFields: Record<string, any> = {}

      if (selected.type === 'client') {
        entityIdFields.client_id = selected.id
      } else if (selected.type === 'prospect') {
        entityIdFields.entity_type = 'prospect'
        entityIdFields.entity_id = selected.id
      } else if (selected.type === 'contact') {
        // entity_type and entity_id as well as contact_id: the timeline needs
        // the first two to render a link back to the person, so setting only
        // contact_id put dead entries in the feed.
        entityIdFields.entity_type = 'contact'
        entityIdFields.entity_id = selected.id
        entityIdFields.contact_id = selected.id
      }

      const commonOptions = {
        title: notes.trim() || `${commType.charAt(0).toUpperCase() + commType.slice(1)} with ${selected.name}`,
        ...entityIdFields,
      }

      if (commType === 'call')    await logCall(commonOptions)
      else if (commType === 'email')   await logEmail(commonOptions)
      else if (commType === 'meeting') await logMeeting(commonOptions)
      else if (commType === 'note')    await logNote(commonOptions)
      else if (commType === 'text')    await logText(commonOptions)
      else {
        await createActivity({
          activity_type: 'other',
          title: commonOptions.title,
          entity_type: selected.type,
          ...entityIdFields,
          source: 'manual',
        })
      }

      showToast(`${commType} logged for ${selected.name}`, 'success')
      onClose()
    } catch (err: any) {
      // The log saved and only the date did not — say that, and close, or the
      // same conversation gets logged twice.
      if (err instanceof TouchNotStampedError) {
        showToast(err.message, 'warning')
        onClose()
        return
      }
      showToast(err.message || 'Failed to log activity', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selected && !saving) {
      e.preventDefault()
      handleSave()
    }
  }

  if (!isOpen) return null

  return (
    <Drawer
      eyebrow="Quick log · ⌘L"
      title="What happened?"
      blurb="Find who it was about, say what happened, save. Enter saves once someone is selected."
      width={520}
      primary="Save log"
      secondary="Discard"
      busy={saving}
      onSubmit={e => { e.preventDefault(); void handleSave() }}
      onClose={onClose}
      footnote="Logging contact is the highest-value habit in the product. Stall detection, contact decay and retention risk are all computed from these entries — an unlogged call is invisible to every warning the desk gives you."
    >
      <Group label="Who it was about" cols={1} aide={searching ? 'Searching…' : undefined}>
        <Field
          label="Athlete, prospect or contact"
          htmlFor="ql-search"
          req
          full
          hint={selected ? `${selected.type} · ${selected.subtitle || 'no detail on file'}` : 'Type at least two characters.'}
        >
          <input
            id="ql-search"
            ref={searchRef}
            type="text"
            value={query}
            onChange={e => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Start typing a name…"
            autoComplete="off"
          />
        </Field>

        {results.length > 0 && (
          <div className={styles.results}>
            {results.map(r => (
              <button
                key={`${r.type}-${r.id}`}
                type="button"
                className={styles.result}
                onClick={() => handleSelect(r)}
              >
                <span className={styles.resultName}>{r.name}</span>
                <span className={styles.resultMeta}>{r.subtitle || r.type}</span>
              </button>
            ))}
          </div>
        )}

        {!searching && query.trim().length >= 2 && results.length === 0 && !selected && (
          <p className={styles.noResults}>
            Nobody on your book matches “{query.trim()}”. Quick log attaches to an existing
            record — add them first, then log it.
          </p>
        )}
      </Group>

      <Group label="What happened" cols={1}>
        <Field label="Type" htmlFor="ql-type" req full>
          <select id="ql-type" value={commType} onChange={e => setCommType(e.target.value as CommType)}>
            {COMM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Note" htmlFor="ql-notes" full optional
          hint="One line is enough. What was said, and what happens next.">
          <textarea
            id="ql-notes"
            ref={notesRef}
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Wants an answer on the guarantee before camp."
          />
        </Field>
      </Group>

      <Impact
        rows={selected ? [
          { label: `Logged against`, value: `${selected.name} · ${selected.type}`, tone: 'primary' },
          { label: 'Last contact', value: 'Resets to today', tone: 'positive' as const },
        ] : []}
        aide="Once someone is selected"
      />
    </Drawer>
  )
}
